/**
 * platform/sections.ts — PHIBIAN CONSOLE'S NAVIGATION, AND WHAT EACH SECTION
 * ACTUALLY READS.
 *
 * Phibian Console (`apps/console`, console.phibian.io) grown into the owner's
 * full operator surface (directives of 2026-10-08,
 * `docs/phibian-console/IMPLEMENTATION.md`). It is still
 * ADR 0015's control plane: its own origin, no tenant session, no row-level
 * tenant data.
 *
 * Every section is one of two things, and the type makes the console say which:
 *
 *   live           — it renders a SERVED platform read. Its data is real.
 *   not_connected  — no served read backs it yet. It renders WHAT it will read,
 *                    WHERE that data lives today and WHAT blocks it. It never
 *                    renders a placeholder number, chart or row: a mock that
 *                    looks like production data is the one thing the directive
 *                    forbids outright.
 *
 * A section moves from `not_connected` to `live` by landing its read, not by
 * editing this list. `console-sections-are-honest.test.ts` holds both halves.
 */
import type { ConsoleOutcome } from './client.js';
import type { ConsoleRequest } from './requests.js';

export type SectionStatus = 'live' | 'not_connected';

/**
 * Navigation groups (owner design-alignment directive, 2026-10-08). Customer
 * onboarding and internal staff access are DIFFERENT groups on purpose: they
 * are separate workflows with separate permissions and identity boundaries.
 */
export const CONSOLE_GROUPS = [
  'Overview',
  'Customers & Onboarding',
  'Operations',
  'Intelligence',
  'Staff & Access',
] as const;
export type ConsoleGroup = (typeof CONSOLE_GROUPS)[number];

export interface ConsoleSection {
  readonly id: string;
  readonly label: string;
  readonly group: ConsoleGroup;
  readonly status: SectionStatus;
  /** One sentence: what an operator gets here. */
  readonly purpose: string;
  /** not_connected only — where the data lives today. */
  readonly source?: string;
  /** not_connected only — the specific thing that has to exist first. */
  readonly blockedOn?: string;
}

export const CONSOLE_SECTIONS: readonly ConsoleSection[] = [
  {
    id: 'overview',
    label: 'Overview',
    group: 'Overview',
    status: 'live',
    purpose: 'Platform readiness, queue health and the customer count, from served reads.',
  },
  {
    id: 'organizations',
    label: 'Organizations',
    group: 'Customers & Onboarding',
    status: 'live',
    purpose: 'The customer directory, provisioning, and each customer’s onboarding state.',
  },
  {
    id: 'onboarding',
    label: 'Onboarding',
    group: 'Customers & Onboarding',
    status: 'live',
    purpose: 'Every customer’s onboarding progress on one board, from the canonical checklist.',
  },
  {
    id: 'adoption',
    label: 'Workers & Adoption',
    group: 'Customers & Onboarding',
    status: 'live',
    purpose:
      'Administrators, supervisors and workers by account status, and invitations by state — ' +
      'counts only, one bound tenant at a time.',
  },
  {
    id: 'integrations',
    label: 'Integrations',
    group: 'Customers & Onboarding',
    status: 'live',
    purpose:
      "Every customer connector: the customer app's own status, last sync and staleness, plus " +
      'failed runs by error code and quarantined records over 7 days.',
  },
  {
    id: 'health',
    label: 'System Health',
    group: 'Operations',
    status: 'live',
    purpose: 'API readiness checks and durable job queue health.',
  },
  {
    id: 'logs',
    label: 'Logs & Errors',
    group: 'Operations',
    status: 'live',
    purpose:
      'Redacted platform logs by source, severity, time and request id, with an error trend. ' +
      'Sources not captured anywhere are named as such.',
  },
  {
    id: 'deployments',
    label: 'Deployments',
    group: 'Operations',
    status: 'live',
    purpose: 'Which build the API is running, as it reports itself.',
  },
  {
    id: 'analytics',
    label: 'Analytics',
    group: 'Intelligence',
    status: 'live',
    purpose:
      'Website traffic, acquisition sources and conversions from PostHog project 653536, as ' +
      'aggregates. Product adoption lives on Customers & Onboarding.',
  },
  {
    id: 'intelligence',
    label: 'AI Intelligence',
    group: 'Intelligence',
    status: 'live',
    purpose:
      'Operational AI metrics across customers: run reliability, tokens and cost, latency, ' +
      'tool-call outcomes, decision evaluation and Phoebe sessions. Counts only, never prompts ' +
      'or answers; what is not captured is said.',
  },
  {
    id: 'knowledge',
    label: 'Global Knowledge',
    group: 'Intelligence',
    status: 'live',
    purpose: 'Review, publish, supersede and retire authoritative external requirements.',
  },
  {
    id: 'access',
    label: 'Staff & Access',
    group: 'Staff & Access',
    status: 'live',
    purpose:
      "Phibian's own Console staff: invitations, roles, two-step status and sessions. " +
      'Separate from customer onboarding, with its own permission.',
  },
];

export type ConsoleSectionId = (typeof CONSOLE_SECTIONS)[number]['id'];

// ---------------------------------------------------------------------------
// GET /ready — the API's own readiness verdict and running build.
// ---------------------------------------------------------------------------

/** Unauthenticated on the API; proxied by this app's nginx (`location = /ready`). */
export function ready(): ConsoleRequest {
  return { method: 'GET', path: '/ready' };
}

/** `HealthResponse` in `packages/shared/src/health.ts`, as far as this screen reads it. */
export interface ReadyBody {
  readonly status?: unknown;
  readonly service?: unknown;
  readonly version?: unknown;
  readonly time?: unknown;
  readonly uptimeSeconds?: unknown;
  readonly checks?: unknown;
}

export interface ReadyCheck {
  readonly name: string;
  readonly status: string;
}

export type ReadyReading =
  | {
      readonly kind: 'read';
      readonly status: string;
      readonly service: string | null;
      readonly version: string | null;
      readonly reportedAt: string | null;
      readonly uptimeSeconds: number | null;
      readonly checks: readonly ReadyCheck[];
    }
  | { readonly kind: 'malformed' }
  | { readonly kind: 'unreadable'; readonly message: string };

const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

/**
 * `/ready` answers 503 WITH a body when a gating check fails. The client maps
 * non-2xx to a refusal and drops the body, so a 503 lands here as `unreadable`
 * with the status in its message — which is still the truth: not ready.
 */
export function describeReady(outcome: ConsoleOutcome<ReadyBody>): ReadyReading {
  if (outcome.kind !== 'ok') return { kind: 'unreadable', message: outcome.message };
  const body = outcome.value;
  const status = str(body?.status);
  if (!status) return { kind: 'malformed' };
  const checks = Array.isArray(body.checks)
    ? body.checks.flatMap((c: unknown) => {
        const name = str((c as { name?: unknown })?.name);
        const s = str((c as { status?: unknown })?.status);
        return name && s ? [{ name, status: s }] : [];
      })
    : [];
  return {
    kind: 'read',
    status,
    service: str(body.service),
    version: str(body.version),
    reportedAt: str(body.time),
    uptimeSeconds: typeof body.uptimeSeconds === 'number' ? body.uptimeSeconds : null,
    checks,
  };
}
